import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import * as FaIcons from "react-icons/fa";
import { useNavigate } from "react-router";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { useParams } from "react-router";
import Swal from "sweetalert2";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import momentTz from "moment-timezone";
import Title from "../GenericComponents/Title";
import { AttendanceStatusBadge } from "./attendanceStatusBadge";
import {
  ATT_TABLE_CELL,
  ATT_TABLE_HEAD,
  ATT_TABLE_HEAD_CENTER,
  ATT_TABLE_ROW,
  AttendanceBtnOutline,
  AttendanceDateInput,
  AttendanceEmpty,
  AttendanceFilterField,
  AttendanceFilterQuickGroup,
  AttendanceFilterRow,
  AttendanceFiltersPanel,
  AttendanceHoursBadge,
  AttendanceKpiCard,
  AttendanceKpiGrid,
  AttendancePageShell,
  AttendancePermissionDenied,
  AttendanceTableCard,
  AttendanceTableWrap,
  AttendanceTimePill,
  employeeInitials,
} from "./attendanceUiShared";
import "../../styles/tailwind.css";

function formatCheckTime(value, timezone) {
  if (!value) return null;
  return momentTz.utc(value).tz(timezone).format("HH:mm");
}

export default function EmployeeMonthlyAttendance({ userId, onBack }) {
  const [editingRecord, setEditingRecord] = useState(null);
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState(null);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const { user_id } = useParams();
  const [data, setData] = useState([]);
  const [user, setUser] = useState({});
  const [editForm, setEditForm] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() =>
    moment().format("YYYY-MM")
  );

  const { startDate, endDate, monthLabel, isCurrentMonth } = useMemo(() => {
    const anchor = moment(selectedMonth, "YYYY-MM", true).isValid()
      ? moment(selectedMonth, "YYYY-MM")
      : moment().startOf("month");
    return {
      startDate: anchor.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: anchor.clone().endOf("month").format("YYYY-MM-DD"),
      monthLabel: anchor.format("MMMM YYYY"),
      isCurrentMonth: anchor.isSame(moment(), "month"),
    };
  }, [selectedMonth]);

  useEffect(() => {
    const result = getPermissions();
    if (result) {
      setPermissions(result);
    }
  }, []);

  useEffect(() => {
    if (user_id) {
      fetchAttendanceData();
    }
  }, [user_id, startDate, endDate]);

  // fetch employee monthly data
  const fetchAttendanceData = async () => {
    if (!user_id || !startDate || !endDate) return;

    try {
      setShowSkeleton(true);
      const response = await apiUrl.get("/emp_attendance_range", {
        params: {
          user_id: user_id,
          start_date: startDate,
          end_date: endDate,
        },
      });
      if (response?.status) {
        setData(response?.data?.data || []);
        setUser(response?.data?.user || {});
      } else {
        setData([]);
        setUser({});
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setData([]);
      setUser({});
    } finally {
      setShowSkeleton(false);
    }
  };

  const handleEdit = (record) => {
    const extractTime = (datetimeStr) => {
      if (!datetimeStr) return "";
      const m = momentTz.utc(datetimeStr).tz(record.userBranchTimeZone);
      return m.format("HH:mm");
    };

    setEditingRecord(record.date);
    setEditForm({
      branch_id: user.branch_id || "",
      user_id: user.id,
      check_in: extractTime(record.check_in),
      check_out: extractTime(record.check_out),
    });
  };

  const handleSave = async (date) => {
    setEditingRecord(null);
    try {
      setShowSkeleton(true);

      const response = await apiUrl.post("/edit_user_attendance", {
        branch_id: editForm.branch_id || "",
        user_id: editForm.user_id,
        check_in: editForm.check_in || "",
        check_out: editForm.check_out || "",
        date: date,
      });
      if (response?.status) {
        Swal.fire({
          title: response?.data?.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        fetchAttendanceData();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowSkeleton(false);
    }
  };

  const handleCancel = () => {
    setEditingRecord(null);
  };

  const rangeSummary = useMemo(() => {
    const stats = {
      days: data.length,
      onTime: 0,
      late: 0,
      absent: 0,
      leave: 0,
    };
    data.forEach((row) => {
      const s = (row.status || "").toLowerCase();
      if (s === "on time" || s === "present") stats.onTime += 1;
      else if (s === "late arrival") stats.late += 1;
      else if (s === "absent") stats.absent += 1;
      else if (s.includes("leave") || s === "holiday" || s === "weekend") stats.leave += 1;
    });
    return stats;
  }, [data]);

  const goToPreviousMonth = () => {
    setSelectedMonth((prev) =>
      moment(prev, "YYYY-MM").subtract(1, "month").format("YYYY-MM")
    );
  };

  const goToNextMonth = () => {
    const next = moment(selectedMonth, "YYYY-MM").add(1, "month");
    if (next.isAfter(moment(), "month")) return;
    setSelectedMonth(next.format("YYYY-MM"));
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(moment().format("YYYY-MM"));
  };

  const updateEditForm = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const exportToExcel = () => {
    if (!data || data.length === 0) {
      Swal.fire({
        title: "No attendance data to export",
        icon: "info",
        toast: true,
        position: "top-end",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    const employeeName = user?.name || "Employee";
    const startDateFormatted = format(new Date(startDate), "MMM d, yyyy");
    const endDateFormatted = format(new Date(endDate), "MMM d, yyyy");
    const dateRangeLabel =
      startDateFormatted === endDateFormatted
        ? startDateFormatted
        : `${startDateFormatted} – ${endDateFormatted}`;

    const rows = [];

    rows.push([`Attendance Report: ${employeeName}`]);
    rows.push([`Period: ${dateRangeLabel}`]);
    rows.push([]);

    rows.push([
      "Date",
      "Check In",
      "Check Out",
      "Working Hours",
      "Status",
      "Notes",
    ]);

    data.forEach((record) => {
      rows.push([
        format(new Date(record.date), "dd MMM yyyy (EEEE)"),
        record.check_in
          ? momentTz
              .utc(record.check_in)
              .tz(record.userBranchTimeZone)
              .format("HH:mm")
          : "--:--",
        record.check_out
          ? momentTz
              .utc(record.check_out)
              .tz(record.userBranchTimeZone)
              .format("HH:mm")
          : "--:--",
        record.working_time || "—",
        record.status || "—",
        record.notes || "—",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);

    const headerRowIndex = 3;
    const headerColumns = ["A", "B", "C", "D", "E", "F"];

    headerColumns.forEach((col) => {
      const cellRef = `${col}${headerRowIndex + 1}`;
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "FFFFFFFF" } },
          fill: { fgColor: { rgb: "FF28A745" } },
          alignment: { horizontal: "center", vertical: "middle" },
        };
      }
    });

    ws["A1"].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center" },
    };

    ws["A2"].s = {
      font: { bold: true, sz: 12, color: { rgb: "FF4B4B4B" } },
      alignment: { horizontal: "center" },
    };

    const colWidths = [
      { wch: 22 }, // Date
      { wch: 15 }, // Check In
      { wch: 15 }, // Check Out
      { wch: 15 }, // Working Hours
      { wch: 18 }, // Status
      { wch: 25 }, // Notes
    ];
    ws["!cols"] = colWidths;

    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    range.e.r = rows.length - 1; // update end row
    range.e.c = 5; // 6 columns (0-5)
    ws["!ref"] = XLSX.utils.encode_range(range);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `${employeeName.replace(/\s+/g, "_")}_Attendance_${format(
      new Date(startDate),
      "yyyy-MM-dd"
    )}_to_${format(new Date(endDate), "yyyy-MM-dd")}.xlsx`;
    FileSaver.saveAs(blob, fileName);
  };
  if (permissions === null) {
    return (
      <>
        <Title title="Employee attendance" />
        <AttendancePageShell embedded={false}>
          <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-500">
            Loading…
          </div>
        </AttendancePageShell>
      </>
    );
  }

  if (permissions.manage_policies !== 1) {
    return (
      <>
        <Title title="Employee attendance" />
        <AttendancePageShell embedded={false}>
          <AttendancePermissionDenied />
        </AttendancePageShell>
      </>
    );
  }

  const hasRows = !showSkeleton && data.length > 0;

  const actionBtnClass =
    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#007BFF] shadow-sm transition hover:bg-gray-50";
  const actionBtnSaveClass =
    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100";

  return (
    <>
      <Title title={user?.name ? `${user.name} — Attendance` : "Employee attendance"} />
      <AttendancePageShell embedded={false}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 shadow-sm">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-[#007BFF]"
              onClick={() => navigate("/attendence")}
            >
              <FaIcons.FaArrowLeft size={11} />
              Back
            </button>
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-[11px] font-bold text-white"
              aria-hidden
            >
              {employeeInitials(user?.name)}
            </span>
            <div className="min-w-0">
              <h1 className="m-0 truncate text-[15px] font-bold text-[#35314d]">
                {user?.name || "Employee"}
              </h1>
              <p className="m-0 truncate text-[11px] text-gray-500">{monthLabel}</p>
            </div>
          </div>
          <AttendanceBtnOutline
            disabled={showSkeleton || data.length === 0}
            onClick={exportToExcel}
            className="!h-8 !px-2.5 !text-[11px] !text-emerald-700"
          >
            <FileExcelOutlined />
            Export
          </AttendanceBtnOutline>
        </div>

        <AttendanceKpiGrid>
          {[
            { label: "Days logged", value: rangeSummary.days, icon: FaIcons.FaCalendarAlt, tone: "primary" },
            { label: "On time", value: rangeSummary.onTime, icon: FaIcons.FaCheckCircle, tone: "success" },
            { label: "Late", value: rangeSummary.late, icon: FaIcons.FaClock, tone: "warning" },
            { label: "Absent", value: rangeSummary.absent, icon: FaIcons.FaUserTimes, tone: "danger" },
            { label: "Leave / off", value: rangeSummary.leave, icon: FaIcons.FaUmbrellaBeach, tone: "info" },
          ].map(({ label, value, icon, tone }) => (
            <AttendanceKpiCard
              key={label}
              label={label}
              icon={icon}
              tone={tone}
              loading={showSkeleton}
              value={value}
            />
          ))}
        </AttendanceKpiGrid>

        <AttendanceFiltersPanel
          compact
          showClearAll={!isCurrentMonth}
          onClearAll={goToCurrentMonth}
          clearAllLabel="This month"
        >
          <AttendanceFilterRow template="1fr" minWidth="16rem">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-[#007BFF] hover:text-[#007BFF]"
                  aria-label="Previous month"
                >
                  <FaIcons.FaChevronLeft size={12} />
                </button>
                <span className="min-w-[9.5rem] text-center text-sm font-bold text-[#35314d]">
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  disabled={isCurrentMonth}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-[#007BFF] hover:text-[#007BFF] disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Next month"
                >
                  <FaIcons.FaChevronRight size={12} />
                </button>
              </div>
              <AttendanceFilterQuickGroup>
                <AttendanceFilterField label="Month" className="!mb-0">
                  <AttendanceDateInput
                    id="att-month-picker"
                    narrow
                    type="month"
                    value={selectedMonth}
                    max={moment().format("YYYY-MM")}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) return;
                      setSelectedMonth(value);
                    }}
                  />
                </AttendanceFilterField>
              </AttendanceFilterQuickGroup>
            </div>
          </AttendanceFilterRow>
        </AttendanceFiltersPanel>

        <AttendanceTableCard
          title="Monthly attendance"
          extra={
            !showSkeleton ? (
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
                {data.length} days
              </span>
            ) : null
          }
        >
          {showSkeleton ? (
            <AttendanceTableWrap>
              <thead>
                <tr>
                  <th className={ATT_TABLE_HEAD}>Date</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Check in</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Check out</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Hours</th>
                  <th className={ATT_TABLE_HEAD}>Status</th>
                  <th className={ATT_TABLE_HEAD}>Notes</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Edit</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className={ATT_TABLE_ROW}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className={ATT_TABLE_CELL}>
                        <div className="h-4 animate-pulse rounded bg-gray-200/80" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </AttendanceTableWrap>
          ) : hasRows ? (
            <AttendanceTableWrap>
              <thead>
                <tr>
                  <th className={ATT_TABLE_HEAD}>Date</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Check in</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Check out</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Hours</th>
                  <th className={ATT_TABLE_HEAD}>Status</th>
                  <th className={ATT_TABLE_HEAD}>Notes</th>
                  <th className={ATT_TABLE_HEAD_CENTER}>Edit</th>
                </tr>
              </thead>
              <tbody>
                {data.map((record) => {
                  const checkIn = formatCheckTime(record.check_in, record.userBranchTimeZone);
                  const checkOut = formatCheckTime(record.check_out, record.userBranchTimeZone);
                  const isEditing = editingRecord === record.date;
                  const isWeekend = [0, 6].includes(new Date(record.date).getDay());

                  return (
                    <tr
                      key={record.date}
                      className={`${ATT_TABLE_ROW} ${isWeekend ? "bg-slate-50/60" : ""}`}
                    >
                      <td className={ATT_TABLE_CELL}>
                        <div className="font-semibold text-gray-900">
                          {format(new Date(record.date), "EEE, d MMM yyyy")}
                        </div>
                      </td>
                      <td className={`${ATT_TABLE_CELL} text-center`}>
                        {isEditing ? (
                          <input
                            type="time"
                            value={editForm?.check_in || ""}
                            onChange={(e) => updateEditForm("check_in", e.target.value)}
                            className="mx-auto block h-9 max-w-[7.5rem] rounded-lg border border-gray-300 px-2 text-sm"
                            step="60"
                          />
                        ) : (
                          <AttendanceTimePill time={checkIn} variant="in" />
                        )}
                      </td>
                      <td className={`${ATT_TABLE_CELL} text-center`}>
                        {isEditing ? (
                          <input
                            type="time"
                            value={editForm?.check_out || ""}
                            onChange={(e) => updateEditForm("check_out", e.target.value)}
                            className="mx-auto block h-9 max-w-[7.5rem] rounded-lg border border-gray-300 px-2 text-sm"
                            step="60"
                          />
                        ) : (
                          <AttendanceTimePill time={checkOut} variant="out" />
                        )}
                      </td>
                      <td className={`${ATT_TABLE_CELL} text-center`}>
                        <AttendanceHoursBadge value={record?.working_time} />
                      </td>
                      <td className={ATT_TABLE_CELL}>
                        <AttendanceStatusBadge status={record.status} />
                      </td>
                      <td className={ATT_TABLE_CELL}>
                        {record.notes ? (
                          <p className="m-0 max-w-[220px] text-[12px] leading-snug text-gray-600">
                            {record.notes}
                          </p>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`${ATT_TABLE_CELL} text-center`}>
                        <div className="flex justify-center gap-1.5">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className={actionBtnSaveClass}
                                onClick={() => handleSave(record.date)}
                                title="Save"
                              >
                                <FaIcons.FaSave size={14} />
                              </button>
                              <button
                                type="button"
                                className={actionBtnClass}
                                onClick={handleCancel}
                                title="Cancel"
                              >
                                <FaIcons.FaTimes size={14} />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={actionBtnClass}
                              onClick={() => handleEdit(record)}
                              title="Edit times"
                            >
                              <FaIcons.FaEdit size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </AttendanceTableWrap>
          ) : (
            <AttendanceEmpty
              icon={FaIcons.FaCalendarTimes}
              title="No attendance in this range"
              description="No records for this month. Try another month using the arrows or month picker."
            />
          )}
        </AttendanceTableCard>
      </AttendancePageShell>
    </>
  );
}
